import { useCallback, useMemo, useState, type MouseEvent } from 'react';
import {
  Box,
  Button,
  InputAdornment,
  Popover,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import { alpha, useTheme } from '@mui/material/styles';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import type { DateCalendarProps } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay, type PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import dayjs, { type Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

export type AdminDateRange = {
  from: string;
  to: string;
};

type Props = {
  label: string;
  value: AdminDateRange;
  onChange: (next: AdminDateRange) => void;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
};

function parseYmd(ymd: string): Dayjs | null {
  if (!ymd) return null;
  const d = dayjs(ymd, 'YYYY-MM-DD', true);
  return d.isValid() ? d.startOf('day') : null;
}

function toYmd(d: Dayjs): string {
  return d.format('YYYY-MM-DD');
}

function formatRangeLabel(from: string, to: string): string {
  const a = parseYmd(from);
  const b = parseYmd(to);
  if (a && b) {
    if (a.isSame(b, 'day')) return a.format('DD MMM YYYY');
    return `${a.format('DD MMM YYYY')} – ${b.format('DD MMM YYYY')}`;
  }
  if (a) return `From ${a.format('DD MMM YYYY')}`;
  if (b) return `Until ${b.format('DD MMM YYYY')}`;
  return '';
}

function dayInRange(day: Dayjs, start: Dayjs | null, end: Dayjs | null): boolean {
  if (!start || !end) return false;
  return !day.isBefore(start, 'day') && !day.isAfter(end, 'day');
}

type RangeDayProps = PickersDayProps & {
  rangeStart: Dayjs | null;
  rangeEnd: Dayjs | null;
  hoverDay: Dayjs | null;
  onDayHover: (day: Dayjs | null) => void;
};

function RangeDay(props: RangeDayProps) {
  const theme = useTheme();
  const {
    day,
    rangeStart,
    rangeEnd,
    hoverDay,
    onDayHover,
    outsideCurrentMonth,
    selected,
    ...other
  } = props;

  let previewStart = rangeStart;
  let previewEnd = rangeEnd;
  if (rangeStart && !rangeEnd && hoverDay) {
    if (hoverDay.isBefore(rangeStart, 'day')) {
      previewStart = hoverDay;
      previewEnd = rangeStart;
    } else {
      previewEnd = hoverDay;
    }
  }

  const isStart = Boolean(previewStart && day.isSame(previewStart, 'day'));
  const isEnd = Boolean(previewEnd && day.isSame(previewEnd, 'day'));
  const inMiddle = dayInRange(day, previewStart, previewEnd) && !isStart && !isEnd;
  const isEndpoint = isStart || isEnd;
  const sameDayRange = isStart && isEnd;

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        ...(inMiddle && { bgcolor: alpha(theme.palette.primary.main, 0.12) }),
        ...(isStart &&
          previewEnd &&
          !sameDayRange && {
            borderTopLeftRadius: '50%',
            borderBottomLeftRadius: '50%',
            bgcolor: alpha(theme.palette.primary.main, 0.12),
          }),
        ...(isEnd &&
          previewStart &&
          !sameDayRange && {
            borderTopRightRadius: '50%',
            borderBottomRightRadius: '50%',
            bgcolor: alpha(theme.palette.primary.main, 0.12),
          }),
      }}
      onMouseEnter={() => onDayHover(day)}
    >
      <PickersDay
        {...other}
        outsideCurrentMonth={outsideCurrentMonth}
        day={day}
        selected={isEndpoint || Boolean(selected)}
        sx={{
          ...(isEndpoint && {
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            fontWeight: 800,
            '&:hover, &:focus': { bgcolor: 'primary.dark' },
          }),
        }}
      />
    </Box>
  );
}

/**
 * Single-field date range: one input + calendar popover with range highlight.
 * Values are `YYYY-MM-DD` (empty when unset).
 */
export function AdminDateRangeField({
  label,
  value,
  onChange,
  fullWidth = true,
  size = 'small',
}: Props) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [draftFrom, setDraftFrom] = useState<Dayjs | null>(null);
  const [draftTo, setDraftTo] = useState<Dayjs | null>(null);
  const [hoverDay, setHoverDay] = useState<Dayjs | null>(null);

  const open = Boolean(anchorEl);
  const display = formatRangeLabel(value.from, value.to);

  const openPicker = (e: MouseEvent<HTMLElement>) => {
    setDraftFrom(parseYmd(value.from));
    setDraftTo(parseYmd(value.to));
    setHoverDay(null);
    setAnchorEl(e.currentTarget);
  };

  const closePicker = () => {
    setAnchorEl(null);
    setHoverDay(null);
  };

  const commitDraft = (from: Dayjs | null, to: Dayjs | null) => {
    onChange({
      from: from ? toYmd(from) : '',
      to: to ? toYmd(to) : '',
    });
  };

  const onDaySelect = (day: Dayjs | null) => {
    if (!day) return;
    const d = day.startOf('day');
    if (!draftFrom || (draftFrom && draftTo)) {
      setDraftFrom(d);
      setDraftTo(null);
      return;
    }
    if (d.isBefore(draftFrom, 'day')) {
      setDraftTo(draftFrom);
      setDraftFrom(d);
      commitDraft(d, draftFrom);
      closePicker();
      return;
    }
    setDraftTo(d);
    commitDraft(draftFrom, d);
    closePicker();
  };

  const clearRange = () => {
    setDraftFrom(null);
    setDraftTo(null);
    onChange({ from: '', to: '' });
    closePicker();
  };

  const onDayHover = useCallback((day: Dayjs | null) => {
    setHoverDay(day);
  }, []);

  const slots = useMemo(
    () =>
      ({
        day: RangeDay,
      }) as DateCalendarProps['slots'],
    [],
  );

  const slotProps = useMemo(
    () =>
      ({
        day: {
          rangeStart: draftFrom,
          rangeEnd: draftTo,
          hoverDay,
          onDayHover,
        },
      }) as DateCalendarProps['slotProps'],
    [draftFrom, draftTo, hoverDay, onDayHover],
  );

  return (
    <>
      <TextField
        size={size}
        fullWidth={fullWidth}
        label={label}
        value={display}
        placeholder="Select range…"
        onClick={openPicker}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <CalendarMonthRoundedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            </InputAdornment>
          ),
          sx: { cursor: 'pointer' },
        }}
        inputProps={{
          'aria-label': label,
          style: { cursor: 'pointer' },
        }}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={closePicker}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            elevation: 8,
            'data-admin-date-range-popover': '',
            sx: {
              mt: 0.75,
              borderRadius: 2.5,
              border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
              boxShadow: `0 16px 40px ${alpha('#0F172A', 0.14)}`,
              overflow: 'hidden',
            },
          } as object,
        }}
        sx={{ zIndex: (t) => t.zIndex.modal + 4 }}
      >
        <Box sx={{ px: 1.5, pt: 1.25, pb: 0.25 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
            {draftFrom && !draftTo
              ? 'Select the end date'
              : 'Select start, then end (same day is OK)'}
          </Typography>
        </Box>
        <Box onMouseLeave={() => setHoverDay(null)}>
          <DateCalendar
            value={draftTo ?? draftFrom}
            onChange={onDaySelect}
            slots={slots}
            slotProps={slotProps}
            sx={{
              width: 320,
              maxHeight: 340,
              '& .MuiPickersDay-root': { fontWeight: 600 },
            }}
          />
        </Box>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ px: 1.5, pb: 1.25, pt: 0.25 }}
        >
          <Button size="small" onClick={clearRange} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Clear
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            {formatRangeLabel(
              draftFrom ? toYmd(draftFrom) : '',
              draftTo ? toYmd(draftTo) : '',
            ) || 'No dates'}
          </Typography>
        </Stack>
      </Popover>
    </>
  );
}
